import { useEffect, useState } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import {
  Activity,
  ClipboardList,
  TrendingDown,
  FileWarning,
  Repeat,
  ExternalLink,
  Loader2,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Client Health Card popover — compact per-partner stats that fetch on demand.
 *
 * Renders a small Activity icon button (unstyled trigger via `asChild`) that,
 * when clicked, opens a popover with 5 clickable stat tiles. Each tile opens
 * the corresponding report in a new tab.
 *
 * Props:
 *   clientCode : partner code (required)
 *   token      : auth token from useAuth()
 */
const ClientHealthPopover = ({ clientCode, token }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !clientCode || !token || stats) return;
    let cancelled = false;
    setLoading(true);
    setError("");
    (async () => {
      try {
        const res = await fetch(
          `${BACKEND_URL}/api/reports/client-health/${encodeURIComponent(clientCode)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!cancelled) setStats(data);
      } catch (err) {
        if (!cancelled) setError(err?.message || "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, clientCode, token, stats]);

  const openReport = (reportType) => {
    const url = `/reports/client/${reportType}/${encodeURIComponent(clientCode)}`;
    window.open(url, "_blank", "noopener");
  };

  const tiles = stats
    ? [
        {
          key: "pending",
          label: "Pending Sales",
          value: stats.pending_sales,
          icon: ClipboardList,
          color: "amber",
          onClick: () => openReport("pending-sales"),
        },
        {
          key: "neg-gp",
          label: "Negative GP",
          value: stats.negative_gp,
          icon: TrendingDown,
          color: "red",
          onClick: () => openReport("negative-gp"),
        },
        {
          key: "no-inv",
          label: "No Invoice",
          value: stats.no_invoice,
          icon: FileWarning,
          color: "orange",
          onClick: () => openReport("no-invoice"),
        },
        {
          key: "reship-free",
          label: "Reship (Free)",
          value: stats.reship_free,
          icon: Repeat,
          color: "purple",
          onClick: () => openReport("reship-audit"),
        },
        {
          key: "reship-paid",
          label: "Reship (Paid)",
          value: stats.reship_paid,
          sub: stats.reship_paid_total > 0
            ? `₹${Math.round(stats.reship_paid_total).toLocaleString("en-IN")}`
            : null,
          icon: Repeat,
          color: "emerald",
          onClick: () => openReport("reship-audit"),
        },
      ]
    : [];

  const colorClasses = {
    amber: "border-amber-200 bg-amber-50 hover:bg-amber-100 text-amber-800",
    red: "border-red-200 bg-red-50 hover:bg-red-100 text-red-800",
    orange: "border-orange-200 bg-orange-50 hover:bg-orange-100 text-orange-800",
    purple: "border-purple-200 bg-purple-50 hover:bg-purple-100 text-purple-800",
    emerald: "border-emerald-200 bg-emerald-50 hover:bg-emerald-100 text-emerald-800",
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          data-testid={`client-health-btn-${clientCode}`}
          title={`Client health — ${clientCode}`}
          className="inline-flex items-center justify-center w-7 h-7 rounded-md text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 transition-colors"
        >
          <Activity className="w-4 h-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-80 p-3"
        onClick={(e) => e.stopPropagation()}
        data-testid={`client-health-popover-${clientCode}`}
      >
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
              Client Health
            </div>
            <div className="text-sm font-bold text-slate-900 font-mono">
              {clientCode}
            </div>
          </div>
          {loading && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
        </div>

        {error && (
          <div className="text-xs text-red-600 bg-red-50 rounded-md p-2 mb-2">
            {error}
          </div>
        )}

        {stats && (
          <div className="grid grid-cols-1 gap-1.5">
            {tiles.map((t) => {
              const Icon = t.icon;
              return (
                <button
                  key={t.key}
                  onClick={t.onClick}
                  data-testid={`health-tile-${t.key}-${clientCode}`}
                  className={`w-full text-left border rounded-lg px-3 py-2 flex items-center gap-2.5 group transition-colors ${colorClasses[t.color]}`}
                >
                  <Icon className="w-4 h-4 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] uppercase tracking-wider font-semibold opacity-80">
                      {t.label}
                    </div>
                    {t.sub && (
                      <div className="text-[11px] opacity-70">{t.sub}</div>
                    )}
                  </div>
                  <div className="text-xl font-bold tabular-nums">{t.value ?? 0}</div>
                  <ExternalLink className="w-3.5 h-3.5 opacity-0 group-hover:opacity-70 transition-opacity" />
                </button>
              );
            })}
          </div>
        )}

        {!loading && !error && stats && (
          <p className="text-[10px] text-slate-400 mt-2 text-center">
            Click any tile to open the full report in a new tab.
          </p>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default ClientHealthPopover;
